import { createContext, useContext, useEffect, useRef, useState } from 'react'

// Minimal scroll context: only flips React state at a single threshold so
// subscribers re-render rarely. Continuous values (scrollY) go straight to
// document.documentElement as a CSS custom prop — no React churn per frame.
const ScrollContext = createContext({ isScrolled: false })

export function ScrollProvider({ children }) {
  const [isScrolled, setIsScrolled] = useState(false)
  const scrolledRef = useRef(false)
  const rafRef = useRef(0)

  useEffect(() => {
    const root = document.documentElement
    const threshold = 24

    const tick = () => {
      rafRef.current = 0
      const y = window.scrollY
      root.style.setProperty('--scroll-y', `${y}px`)
      const next = y > threshold
      if (next !== scrolledRef.current) {
        scrolledRef.current = next
        setIsScrolled(next)
      }
    }

    const onScroll = () => {
      if (rafRef.current) return
      rafRef.current = requestAnimationFrame(tick)
    }

    tick()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <ScrollContext.Provider value={{ isScrolled }}>
      {children}
    </ScrollContext.Provider>
  )
}

export const useScroll = () => useContext(ScrollContext)
